import { Questionnaire } from '@typings/Questionnaire';
import React, { useState } from 'react';
import { Input } from '@components/DesignLibrary/Atoms';

import MinusCircle from '@components/Icons/MinusCircle';
import Buttons from '@components/Buttons';
import PlusCircle from '@components/Icons/PlusCircle';
import { nanoid } from 'nanoid';
import Button from '@components/DesignLibrary/Button';
import Conditions from '@components/Questionnaire/questionnaireEditor/Conditions';
import Spacer from '@components/Spacer';
import s from './ColumnEditor.module.scss';

type Props = {
  field: Questionnaire.FieldTable;
  onUpdateField: (updatedField: Questionnaire.Field) => void;
};

const ColumnEditor: React.FC<Props> = ({ field, onUpdateField }) => {
  const [columns, setColumns] = useState(field.tableColumns ?? []);

  const handleColumnChange = (index: number, key: string, value: unknown): void => {
    setColumns((prev) => prev.map((column, idx) => {
      if (index === idx) {
        return {
          ...column,
          [key]: value,
        };
      }
      return column;
    }));
  };

  const handleRemoveColumn = (index: number): void => {
    setColumns(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleAddColumn = (): void => {
    setColumns(prev => [
      ...prev,
      {
        conditionExpression: null,
        conditionExpressionId: null,
        fieldTemplateId: field.id,
        id: nanoid(),
        label: '',
        rank: prev.length,
      }]);
  };

  const handleSave = (): void => {
    const updatedField: Questionnaire.Field = {
      ...field,
      // @todo - remove when this is typed better
      aiDeduplicationLogic: null,
      isVirtual: false,
      tableColumns: columns,
    };
    onUpdateField(updatedField);
  };

  const setColumnConditionExpression = (idx: number): Parameters<typeof Conditions>[0]['setConditionExpression'] => (expression): void => {
    setColumns(prev => prev.map((column, index) => {
      if (index === idx) {
        return {
          ...column,
          conditionExpression: expression,
        };
      }
      return column;
    }));
  };

  return (
    <div className={s.columnEditor}>
      {columns.map((column, idx) => (
        <div className={s.column} key={column.id}>
          <Input
            label=''
            description="Label"
            value={column.label}
            onChange={(value) => handleColumnChange(idx, 'label', value)}
          />
          <Spacer size={16} />
          <Conditions
            conditionExpression={column.conditionExpression || undefined}
            setConditionExpression={setColumnConditionExpression(idx)}
          />
          <Buttons isWrapper className={s.removeColumnButton} onClick={() => handleRemoveColumn(idx)}>
            <MinusCircle />
          </Buttons>
        </div>
      ))}
      <button
        className={s.addColumnButton}
        onClick={handleAddColumn}
      >
        <PlusCircle weight='fill' /> Add Column
      </button>
      <Button size="sm" variant="primary" onClick={handleSave}>Save</Button>
    </div>
  );
};

export default ColumnEditor;
